import { createContext, useContext, ReactNode, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useQueryUnreadCount } from '@/modules/chat/api/hooks'
import { useMatch, useParams } from 'react-router-dom'
import { pendingWithdrawalsQueryOptions } from './usePollingPendingWithdrawals'

interface SidebarBadgesContextValue {
	badges: Record<string, number>
}

const SidebarBadgesContext = createContext<SidebarBadgesContextValue>({
	badges: {},
})

export function SidebarBadgesProvider({ children }: { children: ReactNode }) {
	// Don't fetch unread count when on chat page (user is viewing chats)
	const _isOnChatPage = useMatch({
		path: '/operator/chat',
		end: true,
	})

	const { roomId } = useParams()

	const isOnChatPage = _isOnChatPage && !roomId

	// Fetch unread count only when not on the chat room page
	const { data: unreadData } = useQueryUnreadCount(!isOnChatPage)
	const { data: pendingWithdrawalsData } = useQuery({
		...pendingWithdrawalsQueryOptions,
		enabled: false,
	})

	const badges = useMemo(() => {
		const pendingWithdrawalsCount = pendingWithdrawalsData?.total ?? 0

		// If on chat room page, hide the badge (show 0)
		if (isOnChatPage) {
			return { chatUnreadCount: 0, pendingWithdrawalsCount , chatNotifyCount: 0}
		}

		return {
			chatUnreadCount: unreadData?.unreadCount ?? 0,
			pendingWithdrawalsCount,
			chatNotifyCount: unreadData?.adminTaskCount ?? 0,
		}
	}, [pendingWithdrawalsData?.total, unreadData?.unreadCount,unreadData?.adminTaskCount , isOnChatPage])

	return (
		<SidebarBadgesContext.Provider value={{ badges }}>
			{children}
		</SidebarBadgesContext.Provider>
	)
}

export function useSidebarBadges() {
	return useContext(SidebarBadgesContext)
}
